/*
 * Terms & Conditions governing use of the website. Client engagements are
 * governed by separate signed agreements — see section 5.
 */
import LegalLayout, { Section, P, Sub, Bullets, Callout } from "../components/LegalLayout";

const LAST_UPDATED = "July 31, 2026";

export default function Terms() {
  return (
    <LegalLayout
      kicker="TERMS & CONDITIONS"
      title="Terms & Conditions"
      lastUpdated={LAST_UPDATED}
      documentTitle="Terms & Conditions | Blue Collar AI"
      summary="The rules for using the Blue Collar AI website. Please read them before you use the Site or send us an inquiry."
    >
      <Section heading="1. Agreement to These Terms">
        <P>
          These Terms & Conditions ("Terms") are a legal agreement between you and Blue
          Collar AI, Inc. ("Blue Collar AI," "we," "us," or "our") governing your access to
          and use of the website at www.bluecollarai.online (the "Site").
        </P>
        <P>
          By accessing or using the Site, you agree to be bound by these Terms and by our
          Privacy Policy. If you do not agree, do not use the Site.
        </P>
      </Section>

      <Section heading="2. Who We Are">
        <P>
          Blue Collar AI, Inc. is an artificial intelligence consulting firm and brand holding
          company based in Columbus, Ohio, serving clients worldwide. We design and deploy AI
          tools for towing companies, auto shops, fleets, and the trades, and we operate a
          portfolio of affiliated brands.
        </P>
      </Section>

      <Section heading="3. Use of the Site">
        <P>
          We grant you a limited, non-exclusive, non-transferable, revocable license to access
          and use the Site for your own informational and business purposes. You agree not to:
        </P>
        <Bullets
          items={[
            "Use the Site for any unlawful purpose or in violation of these Terms",
            "Attempt to gain unauthorized access to the Site, its servers, or any connected systems",
            "Interfere with or disrupt the Site, including by introducing malware or overloading our infrastructure",
            "Scrape, harvest, or systematically extract data from the Site by automated means for commercial purposes without our written permission",
            "Copy, reproduce, republish, or redistribute Site content except as permitted in section 7",
            "Misrepresent your identity or affiliation with any person or organization",
            "Use the Site to transmit unsolicited advertising or spam",
          ]}
        />
      </Section>

      <Section heading="4. Informational Purposes Only — No Professional Advice">
        <Callout>
          Content on the Site is provided for general informational purposes. It is not legal,
          financial, tax, insurance, or other professional advice, and it does not create a
          consultant-client, broker-client, or any other professional relationship.
        </Callout>
        <P>
          Do not act, or refrain from acting, on the basis of Site content without seeking
          advice from a qualified professional about your specific situation. Descriptions of
          our insurance-related services are descriptions of preparation and consulting
          services only — nothing on the Site is an offer to sell insurance, a quote, or a
          binding of coverage.
        </P>
      </Section>

      <Section heading="5. Services and Separate Agreements">
        <P>
          The Site describes services we offer. It is not an offer or a contract to provide
          them. Any engagement between you and Blue Collar AI is governed by a separate
          written agreement signed by both parties, which sets out scope, deliverables,
          timelines, fees, and terms. If anything in these Terms conflicts with a signed
          engagement agreement, the signed agreement controls for that engagement.
        </P>
        <P>
          Timelines mentioned on the Site — such as typical deployment windows — are estimates
          based on past work, not commitments.
        </P>
      </Section>

      <Section heading="6. Inquiries and Communications">
        <P>
          When you submit an inquiry through the Site or contact us by email or phone, you
          agree that we may contact you at the address or number you provide in order to
          respond. Submitting an inquiry does not create a contract or obligate either of us
          to proceed.
        </P>
        <P>
          Do not send confidential or proprietary information through the Site's contact form
          or by unencrypted email. Information you send before a signed agreement or
          confidentiality agreement is in place is not treated as confidential.
        </P>
      </Section>

      <Section heading="7. Intellectual Property">
        <P>
          The Site and its contents — including text, graphics, images, logos, brand names,
          page design, and the selection and arrangement of that material — are owned by Blue
          Collar AI, Inc. or its licensors and are protected by United States and
          international intellectual property laws.
        </P>
        <P>
          "Blue Collar AI" and the names of our affiliated brands are our trademarks or
          service marks. You may not use them without our prior written permission. You may
          view, download, and print pages from the Site for your own internal business
          reference, provided you do not remove any proprietary notices and do not reproduce
          or distribute the material commercially.
        </P>
      </Section>

      <Section heading="8. Third-Party Sites and Affiliated Brands">
        <P>
          The Site links to websites operated by our affiliated brands and by third parties.
          We provide those links for convenience. We do not control third-party sites, and we
          are not responsible for their content, products, services, terms, or privacy
          practices. Accessing a linked site is at your own risk and subject to that site's
          own terms.
        </P>
        <P>
          Some brands shown in our portfolio may be in development, operated by affiliated
          entities, or offered under separate terms. Their appearance on the Site is not a
          warranty about their availability or performance.
        </P>
      </Section>

      <Section heading="9. Artificial Intelligence Disclosures">
        <P>
          Our services involve artificial intelligence systems, including large language
          models. You acknowledge that:
        </P>
        <Bullets
          items={[
            "AI systems can produce output that is inaccurate, incomplete, outdated, or unsuitable for a particular purpose",
            "AI output should be reviewed by a qualified human before being relied on for any decision that carries legal, financial, safety, or operational consequences",
            "We do not warrant that any AI system will be error-free, uninterrupted, or will produce any particular business result",
            "Third-party AI providers may change, deprecate, or restrict their models and services in ways outside our control",
          ]}
        />
      </Section>

      <Section heading="10. No Guarantee of Results">
        <P>
          Any performance figures, case studies, testimonials, or examples on the Site reflect
          particular situations and are not guarantees. Results depend on factors specific to
          your business, including your market, operations, data, and execution. We do not
          promise any specific revenue increase, cost reduction, response time, or other
          outcome.
        </P>
      </Section>

      <Section heading="11. Disclaimer of Warranties">
        <P>
          THE SITE AND ALL CONTENT ARE PROVIDED "AS IS" AND "AS AVAILABLE," WITHOUT WARRANTIES
          OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY. TO THE FULLEST EXTENT PERMITTED
          BY LAW, BLUE COLLAR AI DISCLAIMS ALL WARRANTIES, INCLUDING IMPLIED WARRANTIES OF
          MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.
        </P>
        <P>
          We do not warrant that the Site will be uninterrupted, timely, secure, or error-free,
          that defects will be corrected, or that the Site is free of viruses or other harmful
          components.
        </P>
      </Section>

      <Section heading="12. Limitation of Liability">
        <P>
          TO THE FULLEST EXTENT PERMITTED BY LAW, BLUE COLLAR AI, INC. AND ITS OFFICERS,
          DIRECTORS, EMPLOYEES, AND AGENTS WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
          SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR ANY LOSS OF PROFITS,
          REVENUE, DATA, BUSINESS, OR GOODWILL, ARISING OUT OF OR RELATING TO YOUR USE OF THE
          SITE, WHETHER BASED ON WARRANTY, CONTRACT, TORT, OR ANY OTHER LEGAL THEORY, EVEN IF
          WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
        </P>
        <P>
          OUR TOTAL LIABILITY FOR ALL CLAIMS RELATING TO THE SITE WILL NOT EXCEED ONE HUNDRED
          U.S. DOLLARS ($100). Some jurisdictions do not allow the exclusion or limitation of
          certain damages, so some of the above may not apply to you.
        </P>
      </Section>

      <Section heading="13. Indemnification">
        <P>
          You agree to indemnify, defend, and hold harmless Blue Collar AI, Inc. and its
          officers, directors, employees, and agents from any claims, liabilities, damages,
          losses, and expenses — including reasonable attorneys' fees — arising out of or
          related to your use of the Site, your violation of these Terms, or your violation of
          any rights of another party.
        </P>
      </Section>

      <Section heading="14. Privacy">
        <P>
          Our handling of information is described in our Privacy Policy, which is incorporated
          into these Terms by reference.
        </P>
      </Section>

      <Section heading="15. Changes to the Site and These Terms">
        <P>
          We may modify, suspend, or discontinue any part of the Site at any time without
          notice. We may also revise these Terms; when we do, we will update the "Last
          updated" date at the top of this page. Your continued use of the Site after a
          revision means you accept the updated Terms.
        </P>
      </Section>

      <Section heading="16. Governing Law and Venue">
        <P>
          These Terms are governed by the laws of the State of Ohio, without regard to its
          conflict-of-laws rules. You agree that any dispute arising out of or relating to
          these Terms or the Site will be brought exclusively in the state or federal courts
          located in Franklin County, Ohio, and you consent to the personal jurisdiction of
          those courts.
        </P>
      </Section>

      <Section heading="17. General">
        <Sub>Severability</Sub>
        <P>
          If any provision of these Terms is found unenforceable, that provision will be
          limited or removed to the minimum extent necessary, and the remaining provisions
          will stay in full force.
        </P>
        <Sub>No waiver</Sub>
        <P>
          Our failure to enforce any provision of these Terms is not a waiver of our right to
          do so later.
        </P>
        <Sub>Entire agreement</Sub>
        <P>
          These Terms and our Privacy Policy are the entire agreement between you and Blue
          Collar AI regarding the Site, and they supersede any prior understandings on that
          subject. They do not supersede a signed engagement agreement, which controls the
          services it covers.
        </P>
        <Sub>Assignment</Sub>
        <P>
          You may not assign these Terms without our written consent. We may assign them in
          connection with a merger, acquisition, or sale of assets.
        </P>
      </Section>

      <Section heading="18. Contact Us">
        <P>Questions about these Terms? Get in touch:</P>
        <Bullets
          items={[
            <>
              Email:{" "}
              <a
                href="mailto:chris@bluecollarai.online"
                className="text-electric hover:text-white transition-colors underline underline-offset-4"
              >
                chris@bluecollarai.online
              </a>
            </>,
            <>
              Phone:{" "}
              <a
                href="tel:+16146337935"
                className="text-electric hover:text-white transition-colors underline underline-offset-4"
              >
                +1 (614) 633-7935
              </a>
            </>,
            <>Blue Collar AI, Inc. — Columbus, Ohio, United States</>,
          ]}
        />
      </Section>
    </LegalLayout>
  );
}
